import React, { useState, useEffect } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import axiosInstance from "../config/axios";
import { applyLeaseCommissionDefaults } from "../utils/leaseCommissionDefaults";
import { todayLocalYMD } from "../utils/dateHelpers";

const KeyInfoForm = ({
  keyInfoData,
  handleKeyInfoChange,
  goToNextSection,
  lookupSuccess,
  lookupError,
  isLoading,
  isLoadingTradeNumber,
  nextTradeNumber,
  displayTradeNumber, // effective trade number (e.g. 279-A when pre-construction)
  preConstruction = false,
  installments = null, // 2 or 3 when pre-construction
  preConstructionUploadNumber = null,
  onPreConstructionChange, // (enabled, installments, uploadNumber) => void
  listings,
  setKeyInfoData,
  onListingSelected,
  trades = [], // Add trades prop to check for used listings
  currentTradeNumber = null, // Add current trade number to exclude from check
}) => {
  const [conditions, setConditions] = useState(keyInfoData.conditions || []);
  const [showInstallmentModal, setShowInstallmentModal] = useState(false);
  const [pendingInstallments, setPendingInstallments] = useState(null);
  const [uploadOptions, setUploadOptions] = useState([]);
  const [selectedUploadNumber, setSelectedUploadNumber] = useState(null);
  const [loadingUploads, setLoadingUploads] = useState(false);
  const [newCondition, setNewCondition] = useState({
    conditionText: "",
    dueDate: "",
    conditionMetDate: "",
  });

  // Update conditions when keyInfoData changes (for edit mode)
  useEffect(() => {
    if (keyInfoData.conditions) {
      setConditions(keyInfoData.conditions);
    }
  }, [keyInfoData.conditions]);

  const handleAddCondition = () => {
    console.log("handleAddCondition called");
    console.log("newCondition state:", newCondition);
    console.log("Condition field:", newCondition.conditionText);
    console.log("Due Date field:", newCondition.dueDate);
    console.log("Condition Met Date field:", newCondition.conditionMetDate);

    if (newCondition.conditionText && newCondition.dueDate) {
      const conditionToAdd = {
        conditionText: newCondition.conditionText,
        dueDate: newCondition.dueDate,
        conditionMetDate: newCondition.conditionMetDate,
      };

      console.log("Adding condition:", conditionToAdd);

      const updatedConditions = [...conditions, conditionToAdd];
      setConditions(updatedConditions);
      setNewCondition({
        conditionText: "",
        dueDate: "",
        conditionMetDate: "",
      });

      // Update the keyInfoData with the new conditions
      setKeyInfoData((prev) => {
        const updated = {
          ...prev,
          conditions: updatedConditions,
        };
        console.log("Updated keyInfoData:", updated);
        return updated;
      });
    } else {
      console.log("Validation failed - missing required fields");
    }
  };

  const handleDeleteCondition = (index) => {
    const conditionToDelete = conditions[index];
    const isConfirmed = window.confirm(
      `Are you sure you want to delete the condition: "${conditionToDelete.conditionText}"?`
    );

    if (isConfirmed) {
      const updatedConditions = conditions.filter((_, i) => i !== index);
      setConditions(updatedConditions);

      // Update the keyInfoData with the updated conditions
      setKeyInfoData((prev) => ({
        ...prev,
        conditions: updatedConditions,
      }));
    }
  };

  const handleConditionChange = (field, value) => {
    setNewCondition((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Trade Record</h2>
        <h2 className="text-xl font-bold">Dates</h2>
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          goToNextSection();
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Trade Number and Status */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trade Number
                </label>
                <input
                  type="text"
                  name="tradeNumber"
                  value={displayTradeNumber ?? nextTradeNumber}
                  readOnly
                  className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
                {isLoadingTradeNumber && (
                  <p className="text-sm text-gray-500 mt-1">Loading...</p>
                )}
                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preConstruction}
                    onChange={async (e) => {
                      if (e.target.checked) {
                        setPendingInstallments(null);
                        setSelectedUploadNumber(
                          preConstructionUploadNumber
                            ? {
                                value: preConstructionUploadNumber,
                                label: `#${preConstructionUploadNumber}`,
                              }
                            : null,
                        );
                        setShowInstallmentModal(true);
                        setLoadingUploads(true);
                        try {
                          const res = await axiosInstance.get(
                            "/pre-construction-uploads",
                          );
                          setUploadOptions(
                            (res.data || []).map((row) => ({
                              value: row.uploadNumber,
                              label: `#${row.uploadNumber} (${row.fileCount ?? row.files?.length ?? 0} files)`,
                            })),
                          );
                        } catch (_) {
                          setUploadOptions([]);
                          toast.error(
                            "Could not load pre-construction upload numbers.",
                          );
                        } finally {
                          setLoadingUploads(false);
                        }
                      } else {
                        onPreConstructionChange?.(false, null, null);
                      }
                    }}
                    className="rounded border-gray-300 text-blue-700 focus:ring-blue-700"
                  />
                  <span className="text-sm text-gray-700">Pre construction</span>
                </label>
                {preConstruction && installments ? (
                  <p className="text-xs text-gray-600 mt-1">
                    {installments} installments
                    {preConstructionUploadNumber
                      ? ` · Documents #${preConstructionUploadNumber}`
                      : ""}
                  </p>
                ) : null}
                {showInstallmentModal && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" role="dialog" aria-modal="true" aria-labelledby="installment-modal-title">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                      <h3 id="installment-modal-title" className="text-lg font-semibold text-gray-900 mb-3">
                        Pre construction setup
                      </h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Select the number of installments, then choose the
                        document upload number (from Pre Construction Uploads).
                      </p>
                      <div className="flex gap-3 mb-4">
                        <button
                          type="button"
                          onClick={() => setPendingInstallments(2)}
                          className={`flex-1 py-2 px-4 rounded-md border ${
                            pendingInstallments === 2
                              ? "border-blue-900 bg-blue-50 text-blue-900"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          } focus:ring-2 focus:ring-blue-700`}
                        >
                          2 installments
                        </button>
                        <button
                          type="button"
                          onClick={() => setPendingInstallments(3)}
                          className={`flex-1 py-2 px-4 rounded-md border ${
                            pendingInstallments === 3
                              ? "border-blue-900 bg-blue-50 text-blue-900"
                              : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                          } focus:ring-2 focus:ring-blue-700`}
                        >
                          3 installments
                        </button>
                      </div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Document number
                      </label>
                      <Select
                        options={uploadOptions}
                        value={selectedUploadNumber}
                        onChange={setSelectedUploadNumber}
                        isClearable
                        isLoading={loadingUploads}
                        placeholder={
                          loadingUploads
                            ? "Loading…"
                            : "Select upload # (7000+)"
                        }
                        className="mb-2"
                      />
                      {!uploadOptions.length && !loadingUploads ? (
                        <p className="text-xs text-amber-700 mb-3">
                          No upload numbers yet. Open Upload Documents → Pre
                          Construction Uploads to create one starting at 7000.
                        </p>
                      ) : (
                        <p className="text-xs text-gray-500 mb-3">
                          Optional but recommended — documents under this number
                          will appear on the trade after you save.
                        </p>
                      )}
                      <button
                        type="button"
                        disabled={!pendingInstallments}
                        onClick={() => {
                          onPreConstructionChange?.(
                            true,
                            pendingInstallments,
                            selectedUploadNumber?.value ?? null,
                          );
                          setShowInstallmentModal(false);
                          setPendingInstallments(null);
                        }}
                        className="w-full py-2 px-4 rounded-md bg-blue-900 text-white hover:bg-blue-800 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        Confirm
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowInstallmentModal(false);
                          setPendingInstallments(null);
                        }}
                        className="mt-2 w-full py-2 text-sm text-gray-600 hover:text-gray-900"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={keyInfoData.status}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                >
                  <option value="AVAILABLE">Available</option>
                  <option value="SOLD">Sold</option>
                  <option value="CLOSED">Closed</option>
                  <option value="LEASED">Leased</option>
                  <option value="EXPIRED">Expired</option>
                  <option value="PENDING">Pending</option>
                  <option value="FALLEN THRU">Fallen Thru</option>
                </select>
              </div>
            </div>
            {/* Listing Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Listing Number *
              </label>
              <Select
                options={listings
                  .sort((a, b) => b.listingNumber - a.listingNumber)
                  .map((listing) => {
                    // Check if this listing is already used in any trade (excluding current trade)
                    const isUsed = trades.some(
                      (trade) =>
                        trade.keyInfo?.listingNumber ===
                          listing.listingNumber.toString() &&
                        trade.tradeNumber !== currentTradeNumber
                    );

                    return {
                      value: listing.listingNumber.toString(),
                      label: `${listing.listingNumber} - ${
                        listing.address.streetNumber
                      } ${listing.address.streetName}${
                        isUsed ? " (Already Used)" : ""
                      }`,
                      listing, // pass the whole listing for easy access
                      isUsed, // flag to indicate if listing is already used
                      isDisabled: isUsed, // disable used listings
                    };
                  })}
                value={
                  keyInfoData.listingNumber
                    ? {
                        value: keyInfoData.listingNumber,
                        label: listings.find(
                          (l) =>
                            l.listingNumber.toString() ===
                            keyInfoData.listingNumber
                        )
                          ? `${keyInfoData.listingNumber} - ${
                              listings.find(
                                (l) =>
                                  l.listingNumber.toString() ===
                                  keyInfoData.listingNumber
                              ).address.streetNumber
                            } ${
                              listings.find(
                                (l) =>
                                  l.listingNumber.toString() ===
                                  keyInfoData.listingNumber
                              ).address.streetName
                            }`
                          : keyInfoData.listingNumber,
                      }
                    : null
                }
                onChange={(selected) => {
                  if (selected && selected.listing) {
                    // Check if the selected listing is already used
                    if (selected.isUsed) {
                      // Show error toast and don't allow selection
                      toast.error(
                        `Listing ${selected.listing.listingNumber} is already used in another trade. Please select a different listing.`
                      );
                      return;
                    }

                    const selectedListing = selected.listing;
                    setKeyInfoData((prev) => {
                      const next = {
                      ...prev,
                      listingNumber:
                        selectedListing.listingNumber?.toString() ??
                        prev.listingNumber,
                      streetNumber:
                        selectedListing.address?.streetNumber ??
                        prev.streetNumber,
                      streetName:
                        selectedListing.address?.streetName ?? prev.streetName,
                      unit: selectedListing.address?.unit ?? prev.unit,
                      province:
                        selectedListing.address?.province ?? prev.province,
                      postalCode:
                        selectedListing.address?.postalCode ?? prev.postalCode,
                      entryDate: todayLocalYMD(),
                      type: selectedListing.propertyType ?? prev.type,
                      propertyType:
                        selectedListing.propertyType ?? prev.propertyType,
                      dealType: selectedListing.dealType ?? prev.dealType,
                      mlsNumber: selectedListing.mlsNumber ?? prev.mlsNumber,
                      weManage:
                        selectedListing.lead !== undefined
                          ? selectedListing.lead === true ||
                            selectedListing.lead === "Yes"
                            ? "Yes"
                            : "No"
                          : selectedListing.weManage !== undefined
                          ? selectedListing.weManage
                            ? "Yes"
                            : "No"
                          : prev.weManage,
                      listCommission:
                        selectedListing.commission?.list?.toString() ??
                        prev.listCommission,
                      sellCommission:
                        selectedListing.commission?.sell?.toString() ??
                        prev.sellCommission,
                      // Auto-populate city from selected listing
                      city: selectedListing.address?.city ?? prev.city,
                      sellPrice: prev.sellPrice,
                      };
                      return applyLeaseCommissionDefaults(next, {
                        overwrite: true,
                      });
                    });
                    // Call onListingSelected if provided
                    if (typeof onListingSelected === "function") {
                      onListingSelected(selectedListing);
                    }
                  } else {
                    setKeyInfoData((prev) => ({
                      ...prev,
                      listingNumber: "",
                    }));
                  }
                }}
                isClearable
                placeholder="Select a listing..."
                className="text-sm"
              />
            </div>
            {/* Address Information */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="streetNumber"
                  value={keyInfoData.streetNumber}
                  onChange={handleKeyInfoChange}
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Street Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="streetName"
                  value={keyInfoData.streetName}
                  onChange={handleKeyInfoChange}
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unit
                </label>
                <input
                  type="text"
                  name="unit"
                  value={keyInfoData.unit}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={keyInfoData.city}
                  onChange={handleKeyInfoChange}
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Province <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="province"
                  value={keyInfoData.province}
                  onChange={handleKeyInfoChange}
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Postal Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="postalCode"
                value={keyInfoData.postalCode}
                onChange={handleKeyInfoChange}
                required
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
              />
            </div>
            {/* MLS Number, Property Type, Deal Type */}
            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  MLS Number
                </label>
                <input
                  type="text"
                  name="mlsNumber"
                  value={keyInfoData.mlsNumber || ""}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Type
                </label>
                <select
                  name="propertyType"
                  value={keyInfoData.propertyType || ""}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                  required
                >
                  <option value="">Select Property Type</option>
                  <option value="Commercial">Commercial</option>
                  <option value="Residential">Residential</option>
                  <option value="Condominium">Condominium</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deal Type
                </label>
                <select
                  name="dealType"
                  value={keyInfoData.dealType || ""}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                  required
                >
                  <option value="">Select Deal Type</option>
                  <option value="Sale">Sale</option>
                  <option value="Lease">Lease</option>
                </select>
              </div>
            </div>
            {/* Property Information */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  We Manage
                </label>
                <select
                  name="weManage"
                  value={keyInfoData.weManage}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Classification
                </label>
                <select
                  name="classification"
                  value={keyInfoData.classification}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                >
                  <option value="LISTING SIDE">Listing Side</option>
                  <option value="CO-OPERATING SIDE">Co-operating Side</option>
                  <option value="AGENT DOUBLE ENDER">Agent Double Ender</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Listing Commission
                </label>
                <input
                  type="text"
                  name="listCommission"
                  value={keyInfoData.listCommission}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Co-Operating Side commission
                </label>
                <input
                  type="text"
                  name="sellCommission"
                  value={keyInfoData.sellCommission}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sell Price
              </label>
              <input
                type="text"
                name="sellPrice"
                value={keyInfoData.sellPrice}
                onChange={handleKeyInfoChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
              />
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Dates */}
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Offer Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="offerDate"
                  value={keyInfoData.offerDate}
                  onChange={handleKeyInfoChange}
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firm Date <span className="text-red-500">*</span>
                </label>
                {conditions.length > 0 ? (
                  <input
                    type="date"
                    name="firmDate"
                    value={keyInfoData.firmDate}
                    readOnly
                    required
                    className="block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                  />
                ) : (
                  <input
                    type="date"
                    name="firmDate"
                    value={keyInfoData.firmDate}
                    onChange={handleKeyInfoChange}
                    required
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                  />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Close Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="closeDate"
                  value={keyInfoData.closeDate}
                  onChange={handleKeyInfoChange}
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Entry Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  name="entryDate"
                  value={keyInfoData.entryDate}
                  onChange={handleKeyInfoChange}
                  required
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Finalized Date
              </label>
              <input
                type="date"
                name="finalizedDate"
                value={keyInfoData.finalizedDate}
                onChange={handleKeyInfoChange}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
              />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  We Manage
                </label>
                <select
                  name="weManage"
                  value={keyInfoData.weManage}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Firm
                </label>
                <select
                  name="firm"
                  value={keyInfoData.firm}
                  onChange={handleKeyInfoChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Conditions Section */}
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Conditions
          </h3>

          {/* Add Condition Form */}
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condition *
                </label>
                <select
                  value={newCondition.conditionText}
                  onChange={(e) =>
                    handleConditionChange("conditionText", e.target.value)
                  }
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                >
                  <option value="">Select Condition</option>
                  <option value="Finance">Finance</option>
                  <option value="Inspections">Inspections</option>
                  <option value="Status Certificate">Status Certificate</option>
                  <option value="Lawyers Approval">Lawyers Approval</option>
                  <option value="Due Diligence">Due Diligence</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Due Date *
                </label>
                <input
                  type="date"
                  value={newCondition.dueDate}
                  onChange={(e) =>
                    handleConditionChange("dueDate", e.target.value)
                  }
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Condition Met Date
                </label>
                <input
                  type="date"
                  value={newCondition.conditionMetDate}
                  onChange={(e) => {
                    handleConditionChange("conditionMetDate", e.target.value);
                    if (conditions.length >= 0 && e.target.value) {
                      // Set Firm Date to the latest Condition Met Date
                      setKeyInfoData((prev) => ({
                        ...prev,
                        firmDate: e.target.value,
                        status: "SOLD", // Automatically change status to Sold when condition met date is entered
                      }));
                    }
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={handleAddCondition}
                disabled={!newCondition.conditionText || !newCondition.dueDate}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-green-300 disabled:cursor-not-allowed"
              >
                Add Condition
              </button>
            </div>
          </div>

          {/* Conditions List */}
          {conditions.length > 0 && (
            <div className="space-y-3">
              <h4 className="text-md font-medium text-gray-700">
                Added Conditions:
              </h4>
              {conditions.map((condition, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Condition:
                          </span>
                          <p className="text-sm text-gray-900">
                            {condition.conditionText}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Due Date:
                          </span>
                          <p className="text-sm text-gray-900">
                            {condition.dueDate}
                          </p>
                        </div>
                        <div>
                          <span className="text-sm font-medium text-gray-500">
                            Met Date:
                          </span>
                          <input
                            type="date"
                            value={condition.conditionMetDate || ""}
                            onChange={(e) => {
                              // Update the condition's met date
                              const updatedConditions = conditions.map((c, i) =>
                                i === index
                                  ? { ...c, conditionMetDate: e.target.value }
                                  : c
                              );
                              setConditions(updatedConditions);
                              setKeyInfoData((prev) => ({
                                ...prev,
                                conditions: updatedConditions,
                                firmDate: e.target.value, // Set Firm Date to the latest Condition Met Date
                                status: "SOLD", // Automatically change status to Sold when condition met date is entered
                              }));
                            }}
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-700 focus:ring-blue-700"
                          />
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleDeleteCondition(index)}
                      className="ml-4 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:bg-yellow-300"
          >
            Next
          </button>
        </div>
      </form>
      {/* Status Messages */}
      {(lookupSuccess || lookupError) && (
        <div className="mt-4">
          {lookupSuccess && (
            <div className="p-3 bg-green-100 border border-green-300 rounded-md text-green-800">
              {lookupSuccess}
            </div>
          )}
          {lookupError && (
            <div className="p-3 bg-red-100 border border-red-300 rounded-md text-red-800">
              {lookupError}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default KeyInfoForm;
